import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AuctionFeedResponse,
  AuctionListing,
  CrzContract,
  CrzFeedResponse,
  InsolvencyFeedResponse,
} from '@marstein-radar/shared';
import { isIco, formatApprox, riskBand, riskWord, formatEur } from '../styles';
import { TerminalRegistry } from '../components/TerminalRegistry';
import { SLOVAKIA_KRAJE, SLOVAKIA_KRAJE_VIEWBOX } from '../data/slovakia-kraje';

interface RegionCount {
  region: string;
  count: number;
}

// Celkový počet firiem a živnostníkov (user request 2026-07-19) — natvrdo namiesto
// živého /api/v1/companies/count na každom page loade: číslo rastie extrémne
// pomaly (rádovo desiatky denne oproti 2,1M celkovo), netreba kvôli nemu robiť
// request pri každom reloade landing page. Aktualizovať zhruba raz mesačne
// (alebo keď sa reálny počet výraznejšie posunie) — pozri /api/v1/companies/count.
const TOTAL_COMPANIES = 2_138_628;

// Rovnaký dôvod ako TOTAL_COMPANIES vyššie — statband potreboval čakať na
// /api/v1/insolvency/feed (pomalý dotaz) len kvôli súhrnnému číslu, ktoré sa mení
// rádovo o pár desiatok denne. Živý feed nižšie ("Dnes v registroch") naďalej
// fetchuje naživo, lebo tam ide o dnešné položky, nie o súhrn. Aktualizovať
// spolu s TOTAL_COMPANIES — pozri /api/v1/insolvency/feed?page=1 (pole "total").
const TOTAL_INSOLVENCIES = 74_074;

// Landing feed (v3 B+C štúdia, 2026-07-18) — obsah, ktorý sa mení každý deň, ako
// dôvod vracať sa. Labels zámerne duplikované z CompanyPage (nie sú exportované;
// malý slovník, zdieľanie by vytvorilo závislosť stránok len kvôli 4 riadkom).
const FEED_EVENT_LABELS: Record<string, string> = {
  konkurz_restrukturalizacia: 'Konkurz/reštrukturalizácia',
  konkurz_vyrovnanie: 'Vyrovnanie',
  likvidacia: 'Likvidácia',
  znizenie_imania: 'Zníženie imania',
};

// "Dnes v registroch" (user request 2026-07-19: čistý insolvenčný feed pôsobil
// jednotvárne — samé "Zníženie základného imania" za sebou) — zlúčenie 3
// zdrojov (insolvencie/dražby/zmluvy) do jedného chronologického feedu, každý
// s vlastnou farbou pilulky, nech je pri pohľade jasné, o aký typ udalosti ide.
export interface MixedFeedItem {
  id: string;
  date: string;
  kind: 'insolvency' | 'auction' | 'crz';
  pillLabel: string;
  title: string;
  subtitle: string | null;
  linkTo: string;
}

// Dražby (predmet) aj zmluvy (subject) občas obsahujú celý odstavec právneho
// textu namiesto krátkeho titulku — bez orezania by jeden riadok rozbil celý
// feed (viď screenshot z ladenia, 2026-07-19).
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

function insolvencyToFeedItem(e: InsolvencyFeedResponse['results'][number]): MixedFeedItem {
  return {
    id: `ins-${e.id}`,
    date: e.eventDate ?? '',
    kind: 'insolvency',
    pillLabel: FEED_EVENT_LABELS[e.eventType] ?? e.eventType,
    title: e.companyName ?? e.ico,
    subtitle: e.city,
    linkTo: `/firma/${e.ico}`,
  };
}

function auctionToFeedItem(a: AuctionListing): MixedFeedItem {
  // auctionDate je dátum KONANIA dražby (často v budúcnosti) — na "čo je nové"
  // triedenie sa nehodí, preto sa na dátum feedu používa publishedAt (kedy
  // pribudla do OV, 100% pokrytie, vždy v minulosti). Dátum konania sa len
  // ukáže v podnadpise pre kontext.
  const when = a.auctionDate ? new Date(a.auctionDate).toLocaleDateString('sk-SK') : null;
  return {
    id: `auc-${a.id}`,
    date: a.publishedAt ?? '',
    kind: 'auction',
    pillLabel: 'Dražba',
    title: truncate(a.predmet ?? a.propertyType ?? 'Dražba', 70),
    subtitle:
      [when ? `koná sa ${when}` : null, a.municipality, a.region].filter(Boolean).join(' · ') ||
      null,
    linkTo: '/drazby',
  };
}

function crzToFeedItem(c: CrzContract): MixedFeedItem {
  return {
    id: `crz-${c.id}`,
    date: c.signedOn ?? '',
    kind: 'crz',
    pillLabel: 'Zmluva',
    title: truncate(c.supplierName ?? c.subject ?? 'Zmluva', 70),
    subtitle: c.contractingAuthorityName ? truncate(c.contractingAuthorityName, 60) : null,
    linkTo: c.supplierIco ? `/firma/${c.supplierIco}` : '/zmluvy',
  };
}

interface CompanySearchHit {
  ico: string;
  name: string;
  city: string | null;
}

// Hero prerobený od nuly (user request 2026-08-02: "predstav si že tam vôbec
// nie je tento blok... nejaká pekná animácia mapy za textom a všetko čo
// dokáže tento náš systém na nejakých kartičkách") — cyklujúci widget s
// jednou ukážkovou firmou nahradený mapou ako plnoplošným pozadím za textom +
// riadkom kartičiek so schopnosťami systému (dáta z home-sources-strip nižšie,
// zhustené na 6 kategórií).
interface HeroCapability {
  title: string;
  desc: string;
  icon: React.JSX.Element;
}

const HERO_CAPABILITIES: HeroCapability[] = [
  {
    title: 'Financie',
    desc: 'Celá história účtovných závierok z RÚZ, nie len posledný rok.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" clipRule="evenodd" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 3h9l5 5v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm2.5 8.4h9v1.7h-9v-1.7Zm0 3.6h9v1.7h-9v-1.7Zm0 3.6h6v1.7h-6v-1.7Z" />
      </svg>
    ),
  },
  {
    title: 'Rizikové skóre',
    desc: 'Vysvetliteľné skóre a Altman Z-skóre na jeden pohľad.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" clipRule="evenodd" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2.3 22.6 21H1.4L12 2.3ZM11.1 9v6h1.8V9h-1.8Zm0 7.6v1.8h1.8v-1.8h-1.8Z" />
      </svg>
    ),
  },
  {
    title: 'Insolvencie a dražby',
    desc: 'REPLIK konania aj dražby z Obchodného vestníka, denne.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <g transform="rotate(45 12 12)">
          <rect x="9" y="2.5" width="6" height="6" rx="1.2" />
          <rect x="10.7" y="8.5" width="2.6" height="9" rx="1.2" />
        </g>
        <rect x="3.5" y="19.5" width="10" height="2.2" rx="1.1" />
      </svg>
    ),
  },
  {
    title: 'Vlastnícke prepojenia',
    desc: 'Graf spoločníkov, konateľov a prepojených firiem.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="18" cy="7" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="11" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 7.3 15.5 7.7M7.2 8.2 10 15.9M17 9 12.5 16" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    title: 'Dlhy voči štátu',
    desc: 'Sociálna poisťovňa, DPH aj index daňovej spoľahlivosti.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2 22 8H2L12 2Z" />
        <rect x="3" y="9.6" width="18" height="1.8" rx="0.6" />
        <rect x="4.5" y="13" width="2" height="6" rx="0.6" />
        <rect x="9" y="13" width="2" height="6" rx="0.6" />
        <rect x="13" y="13" width="2" height="6" rx="0.6" />
        <rect x="17.5" y="13" width="2" height="6" rx="0.6" />
        <rect x="2" y="20.2" width="20" height="1.8" rx="0.6" />
      </svg>
    ),
  },
  {
    title: 'Sledovanie a alerty',
    desc: 'Ulož si firmu do watchlistu a dostaň upozornenie pri zmene.',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2.2A5.6 5.6 0 0 0 6.4 7.8v3.3l-1.9 3.8a1 1 0 0 0 .9 1.4h13.2a1 1 0 0 0 .9-1.4l-1.9-3.8V7.8A5.6 5.6 0 0 0 12 2.2Z" />
        <circle cx="12" cy="20" r="1.7" />
      </svg>
    ),
  },
];

// Hero vizuál prerobený štvrtýkrát (user feedback 2026-08-02, po tom čo sieť/
// rádar/výkaz-pozadie odmietnuté ako "strašne abstraktné" — poslal referenciu
// https://odmeny.marstein.sk/, kde hero ukazuje SKUTOČNÝ produkt — telefón s
// appkou, nie dekoráciu). Namiesto plnoplošného pozadia je teraz konkrétny
// "screenshot" produktu: mockup okna prehliadača s reálnym profilom firmy
// (skóre, Altman Z, dlhy, insolvencie), ktorý cyklicky prepína medzi 3
// ukážkovými firmami — presne to, čo appka reálne zobrazí po vyhľadaní.
interface HeroExample {
  ico: string;
  name: string;
  risk: 'low' | 'mid' | 'high';
  riskLabel: string;
  score: number;
  altman: string;
  social: string;
  insolvency: string;
  city: string;
  region: string;
}
// Ilustračný fallback, kým sa nenačítajú skutočné dáta nižšie (HERO_EXAMPLE_COMPANIES)
// — vidno len na zlomok sekundy pri prvom vykreslení alebo pri výpadku API.
const HERO_EXAMPLES_FALLBACK: HeroExample[] = [
  {
    ico: '31411801',
    name: 'Príklad s. r. o.',
    risk: 'low',
    riskLabel: 'Nízke riziko',
    score: 18,
    altman: '3.4 · Bezpečná zóna',
    social: 'Bez dlhu',
    insolvency: 'Žiadne',
    city: 'Bratislava',
    region: 'Bratislavský kraj',
  },
];

function altmanZoneWord(zone: 'safe' | 'grey' | 'distress'): string {
  if (zone === 'safe') return 'Bezpečná zóna';
  if (zone === 'grey') return 'Sivá zóna';
  return 'Riziková zóna';
}

interface HeroExampleSocpoistDebt {
  amount?: number | null;
}
interface HeroExampleCompanyResponse {
  name: string;
}
interface HeroExampleRiskResponse {
  score: number;
  altmanZScore: { score: number; zone: 'safe' | 'grey' | 'distress' } | null;
}
interface HeroExampleInsolvencyResponse {
  events: unknown[];
  socpoistDebts: HeroExampleSocpoistDebt[];
}

// Skutočné firmy namiesto vymyslených (user request 2026-08-02: "vieme tam
// dať aj nejaké reálne spoločnosti"). Vybrané ručne — nie náhodne z DB —
// keďže veľké, dobre známe firmy s reálnym nenulovým rizikovým skóre sú v
// dátach vzácne (rizikové signály sa takmer výhradne viažu na živnostníkov
// bez tržieb, ktorých meno by sme z princípu na landing page nezobrazovali,
// viď [[firmyradar_exekucie_research]] — natural-person linking bol už raz
// explicitne zamietnutý). BUKÓZA má "v konkurze" priamo vo svojom oficiálnom
// registrovanom mene (ORSR), nie je to nálepka, ktorú by sme jej dávali my.
// staleTime 7 dní ~ "aktualizuje sa raz do týždňa", ako chcel user — v
// skutočnosti vždy živé dáta, len sa nerefetchuje pri každej návšteve.
const HERO_EXAMPLE_ICOS: { ico: string; city: string; region: string }[] = [
  { ico: '31333532', city: 'Bratislava', region: 'Bratislavský kraj' },
  { ico: '48006122', city: 'Dubnica nad Váhom', region: 'Trenčiansky kraj' },
  { ico: '36445266', city: 'Hencovce', region: 'Prešovský kraj' },
];

// Blok 4, Týž. 13 — verejná landing page. Hlavný hák: kompletná história financií
// zadarmo (Finstat ju skrýva za Premium) + 3 podporné karty s ostatnými hotovými
// diferenciátormi (vysvetliteľné skóre, hub graf, real-time alerty). Štýl inšpirovaný
// Crunchbase/Apollo (karty, priestor, jedna akcentová farba) — nie generický
// AI-SaaS vzhľad, viď docs prieskum konkurencie.
export function HomePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [heroExampleIndex, setHeroExampleIndex] = useState(0);
  const [showHeroShotInfo, setShowHeroShotInfo] = useState(false);

  const { data: heroExamplesLive } = useQuery<HeroExample[]>({
    queryKey: ['hero-example-companies'],
    queryFn: async () => {
      return Promise.all(
        HERO_EXAMPLE_ICOS.map(async ({ ico, city, region }) => {
          const [company, risk, insolvency] = await Promise.all([
            fetch(`/api/v1/companies/${ico}`).then(
              (r) => r.json() as Promise<HeroExampleCompanyResponse>,
            ),
            fetch(`/api/v1/companies/${ico}/risk-score`).then(
              (r) => r.json() as Promise<HeroExampleRiskResponse>,
            ),
            fetch(`/api/v1/companies/${ico}/insolvency`).then(
              (r) => r.json() as Promise<HeroExampleInsolvencyResponse>,
            ),
          ]);
          const socialDebtTotal = insolvency.socpoistDebts.reduce(
            (sum, d) => sum + (d.amount ?? 0),
            0,
          );
          return {
            ico,
            name: company.name,
            risk: riskBand(risk.score),
            riskLabel: riskWord(risk.score),
            score: risk.score,
            altman: risk.altmanZScore
              ? `${risk.altmanZScore.score.toFixed(2)} · ${altmanZoneWord(risk.altmanZScore.zone)}`
              : '—',
            social: socialDebtTotal > 0 ? `Dlh ${formatEur(socialDebtTotal)}` : 'Bez dlhu',
            insolvency:
              insolvency.events.length > 0 ? `${insolvency.events.length} záznamov` : 'Žiadne',
            city,
            region,
          } satisfies HeroExample;
        }),
      );
    },
    staleTime: 7 * 24 * 60 * 60_000,
  });
  const heroExamples = heroExamplesLive ?? HERO_EXAMPLES_FALLBACK;

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroExampleIndex((i) => (i + 1) % heroExamples.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [heroExamples.length]);
  const heroExample: HeroExample = heroExamples[heroExampleIndex % heroExamples.length]!;

  // Návrhy sa naťahujú priebežne počas písania (debounce 250ms), rovnako ako
  // vyhľadávanie v hornej lište (TopBar.tsx) — user request 2026-07-19, nech
  // sa hero search správa rovnako ako ten "úplne na vrchu".
  useEffect(() => {
    const q = query.trim();
    if (!q || isIco(q)) {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: results, isFetching } = useQuery<CompanySearchHit[]>({
    queryKey: ['company-search', debounced],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/search?q=${encodeURIComponent(debounced)}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: debounced.length >= 2,
  });

  const { data: registryFeed } = useQuery<InsolvencyFeedResponse>({
    queryKey: ['home-registry-feed'],
    queryFn: async () => {
      const res = await fetch('/api/v1/insolvency/feed?page=1');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: 10 * 60_000,
  });

  const { data: auctionsFeed } = useQuery<AuctionFeedResponse>({
    queryKey: ['home-auctions-feed'],
    queryFn: async () => {
      const res = await fetch('/api/v1/auctions/feed?page=1');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: 10 * 60_000,
  });

  const { data: crzFeed } = useQuery<CrzFeedResponse>({
    queryKey: ['home-crz-feed'],
    queryFn: async () => {
      const res = await fetch('/api/v1/crz/feed?page=1');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: 10 * 60_000,
  });

  // Zdrojové dáta (najmä OV) obsahujú ojedinelé chybné budúce dátumy (napr. 2045)
  // — zahodiť pred zoraďovaním, inak by viseli navrchu donekonečna.
  const isPastDated = (item: MixedFeedItem) => item.date && new Date(item.date) <= new Date();
  const byDateDesc = (a: MixedFeedItem, b: MixedFeedItem) =>
    new Date(b.date).getTime() - new Date(a.date).getTime();

  // Dražby pribúdajú do OV oveľa častejšie než insolvenčné udalosti alebo
  // zmluvy — čisto chronologické zoradenie všetkých troch zdrojov dokopy by
  // feed celý zaplnilo len dražbami (overené pri ladení, 2026-07-19). Preto sa
  // najprv vezme top 3 z KAŽDÉHO zdroja zvlášť (zaručené zastúpenie) a až tento
  // menší výber sa zoradí chronologicky pre finálne zobrazenie.
  const topInsolvency = (registryFeed?.results ?? [])
    .map(insolvencyToFeedItem)
    .filter(isPastDated)
    .sort(byDateDesc)
    .slice(0, 3);
  const topAuctions = (auctionsFeed?.results ?? [])
    .map(auctionToFeedItem)
    .filter(isPastDated)
    .sort(byDateDesc)
    .slice(0, 3);
  const topCrz = (crzFeed?.results ?? [])
    .map(crzToFeedItem)
    .filter(isPastDated)
    .sort(byDateDesc)
    .slice(0, 3);

  const mixedFeed: MixedFeedItem[] = [...topInsolvency, ...topAuctions, ...topCrz].sort(byDateDesc);

  const { data: regionCounts } = useQuery<RegionCount[]>({
    queryKey: ['region-counts'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/region-counts');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    // Počty sa menia pomaly (backend cachuje 24h, viď CompaniesService.regionCounts)
    // — netreba refetch pri každej návšteve landing page.
    staleTime: 24 * 60 * 60_000,
  });

  function submit(): void {
    const q = query.trim();
    if (!q) return;
    if (isIco(q)) {
      navigate(`/firma/${q}`);
    } else {
      setDebounced(q);
    }
  }

  return (
    <>
      <div className="hero-glow-zone">
        <div className="hero-aurora" aria-hidden="true">
          <div className="hero-aurora-blob hero-aurora-blob--1" />
          <div className="hero-aurora-blob hero-aurora-blob--2" />
          <div className="hero-aurora-blob hero-aurora-blob--3" />
          <div className="hero-aurora-blob hero-aurora-blob--4" />
        </div>
      <section className="hero-band">
        <div className="hero-row">
        <div className="hero-top">
        <div className="hero">
          <h1>
            Preverte obchodného partnera <span className="accent">za pár sekúnd</span>.
          </h1>
          <p className="lead">
            Presné finančné a rizikové dáta priamo zo štátnych registrov, pripravené na
            okamžité rozhodovanie. Bez registrácie, zadarmo.
          </p>
          <div className="hero-search">
            <div style={{ position: 'relative' }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
                style={{ display: 'flex', gap: 0, border: '1.5px solid var(--brand)' }}
              >
                <input
                  id="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="IČO alebo názov firmy"
                  style={{
                    flex: 1,
                    padding: '0.9rem 1.1rem',
                    border: 0,
                    fontSize: '1rem',
                    background: 'var(--bg-raised)',
                    color: 'var(--ink)',
                  }}
                  autoFocus
                />
                <button
                  type="submit"
                  style={{
                    padding: '0.9rem 1.5rem',
                    border: 0,
                    background: 'var(--brand)',
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    letterSpacing: '0.02em',
                  }}
                >
                  Hľadať
                </button>
              </form>
              {/* Position: absolute — dropdown pláva nad hero-cite/statband
                  namiesto toho, aby ich odtláčal nadol (user feedback
                  2026-07-19), rovnako ako dropdownStyle v TopBar.tsx. */}
              {isFetching && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    marginTop: '0.5rem',
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    boxShadow: 'var(--shadow-md)',
                    overflow: 'hidden',
                  }}
                >
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={{ padding: '0.7rem 1rem' }}>
                      <div className="skel" style={{ height: 12, width: '55%', marginBottom: 6 }} />
                      <div className="skel" style={{ height: 10, width: '35%' }} />
                    </div>
                  ))}
                </div>
              )}
              {!isFetching && results && debounced.length >= 2 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    marginTop: '0.5rem',
                    textAlign: 'left',
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    boxShadow: 'var(--shadow-md)',
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  {results.length === 0 && (
                    <div
                      style={{ padding: '0.7rem 1rem', color: 'var(--slate)', fontSize: '0.85rem' }}
                    >
                      Žiadna firma nenájdená.
                    </div>
                  )}
                  {results.map((c) => (
                    <button
                      key={c.ico}
                      type="button"
                      onClick={() => navigate(`/firma/${c.ico}`)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '0.6rem 1rem',
                        border: 'none',
                        borderTop: '1px solid var(--line)',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                      }}
                    >
                      {c.name}
                      <span className="num" style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                        {' '}
                        · IČO {c.ico}
                        {c.city ? ` · ${c.city}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <Link to="/vyhladavanie" className="hero-advanced-link">
            Pokročilé vyhľadávanie podľa mesta, kraja či predmetu podnikania
          </Link>
        </div>
        <div className="hero-shot">
          <div className="hero-shot-frame">
            <div className="hero-shot-bar">
              <span className="hero-shot-dot" />
              <span className="hero-shot-dot" />
              <span className="hero-shot-dot" />
              <span className="hero-shot-url">corpuls.sk/firma/{heroExample.ico}</span>
              <div className="hero-shot-info-wrap">
                <button
                  type="button"
                  className="hero-shot-info-btn"
                  aria-label="O týchto dátach"
                  aria-expanded={showHeroShotInfo}
                  onClick={() => setShowHeroShotInfo((v) => !v)}
                >
                  ?
                </button>
                {showHeroShotInfo && (
                  <div className="hero-shot-info-popover" role="tooltip">
                    Reálne spoločnosti z databázy Corpuls.
                  </div>
                )}
              </div>
            </div>
            <div key={heroExampleIndex} className="hero-shot-body">
              <div className="hero-shot-main">
                <div className="hero-shot-info">
                  <div className="hero-shot-top">
                    <span className="hero-shot-name">{heroExample.name}</span>
                    <span className={`pill ${heroExample.risk}`}>{heroExample.riskLabel}</span>
                  </div>
                  <div className="hero-shot-score-row">
                    <div className="hero-shot-score" data-risk={heroExample.risk}>
                      {heroExample.score}
                      <span>/100</span>
                    </div>
                    <div className="hero-shot-bar-track">
                      <div
                        className="hero-shot-bar-fill"
                        data-risk={heroExample.risk}
                        style={{ width: `${heroExample.score}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="hero-shot-map">
                  <svg
                    className="hero-shot-map-svg"
                    viewBox={SLOVAKIA_KRAJE_VIEWBOX}
                    role="img"
                    aria-label={`Sídlo: ${heroExample.city}, ${heroExample.region}`}
                  >
                    {Object.entries(SLOVAKIA_KRAJE).map(([region, d]) => (
                      <path
                        key={region}
                        d={d}
                        className={region === heroExample.region ? 'hero-shot-kraj hero-shot-kraj--active' : 'hero-shot-kraj'}
                      />
                    ))}
                  </svg>
                  <span className="hero-shot-map-city">{heroExample.city}</span>
                </div>
              </div>
              <div className="hero-shot-facts">
                <div>
                  <span>Altman Z</span>
                  <strong>{heroExample.altman}</strong>
                </div>
                <div>
                  <span>Sociálna poisťovňa</span>
                  <strong>{heroExample.social}</strong>
                </div>
                <div>
                  <span>Insolvenčné konania</span>
                  <strong>{heroExample.insolvency}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
        </div>
      </section>

      <section className="statband statband--card">
        <div>
          <div className="v">{formatApprox(TOTAL_COMPANIES)}</div>
          <div className="k">firiem a živnostníkov</div>
        </div>
        <div>
          <div className="v">{formatApprox(TOTAL_INSOLVENCIES)}</div>
          <div className="k">insolvenčných udalostí</div>
        </div>
        <div>
          <div className="v">denne</div>
          <div className="k">synchronizácia registrov</div>
        </div>
        <div>
          <div className="v">Zadarmo</div>
          <div className="k">základné údaje pre každú firmu</div>
        </div>
      </section>

      <div className="hero-capabilities hero-capabilities--standalone">
        {HERO_CAPABILITIES.map((cap) => (
          <div className="hero-cap-card" key={cap.title}>
            <span className="hero-cap-icon">{cap.icon}</span>
            <h4>{cap.title}</h4>
            <p>{cap.desc}</p>
          </div>
        ))}
      </div>
      </div>

      <TerminalRegistry
        mixedFeed={mixedFeed}
        regionCounts={regionCounts}
        totalCompaniesApprox={formatApprox(TOTAL_COMPANIES)}
      />

      <section className="home-quote">
        <blockquote>„Dôvera v biznise sa buduje na faktoch, nie na dobrom dojme.“</blockquote>
        <cite>— filozofia Corpuls</cite>
      </section>

      <section className="home-whom">
        <div className="home-whom-inner">
          <h2>Pre koho je Corpuls</h2>
          <div className="home-whom-grid">
            <div className="home-whom-col">
              <h3>Pre podnikateľov</h3>
              <ul>
                <li>Preveriť obchodného partnera pred podpisom zmluvy</li>
                <li>Sledovať zmeny u konkurencie a dodávateľov</li>
                <li>Skontrolovať dlhy voči štátu a insolvenčné konania</li>
              </ul>
            </div>
            <div className="home-whom-col">
              <h3>Pre investorov a analytikov</h3>
              <ul>
                <li>Rizikové skóre a Altman Z-skóre na jeden pohľad</li>
                <li>Kompletná história financií, nie len posledný rok</li>
                <li>Graf vlastníckych a personálnych prepojení</li>
              </ul>
            </div>
            <div className="home-whom-col">
              <h3>Pre novinárov a výskumníkov</h3>
              <ul>
                <li>Dražby, verejné zákazky a obstarávania na jednom mieste</li>
                <li>Denne aktualizovaný register insolvencií</li>
                <li>Exportovateľné dáta pre ďalšiu analýzu</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="home-blog">
        <div className="home-blog-inner">
          <div className="home-blog-head">
            <h2>Preskúmaj ďalej</h2>
          </div>
          <div className="home-blog-grid">
            <Link to="/metodika" className="home-blog-card">
              <span className="cat">Metodika</span>
              <h3>Ako počítame rizikové skóre a Altman Z</h3>
              <p>Presný postup a váhy za vysvetliteľným skóre — žiadna čierna skrinka.</p>
            </Link>
            <Link to="/zmluvy/rizikovi-dodavatelia" className="home-blog-card">
              <span className="cat">Rebríček</span>
              <h3>Rizikoví dodávatelia verejného obstarávania</h3>
              <p>Firmy s aktívnymi zákazkami od štátu, ktoré zároveň nesú rizikové signály.</p>
            </Link>
            <Link to="/osoby" className="home-blog-card">
              <span className="cat">Osoby</span>
              <h3>Vlastnícke a personálne prepojenia naprieč firmami</h3>
              <p>Kto stojí za koľkými firmami naraz — vyhľadávanie podľa konateľa či spoločníka.</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
